import { isBefore, startOfDay } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard16, Warning } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Toggle } from 'shared/components/ui/toggle';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import {
  HabitSchedule,
  ScheduleCompletionActions,
} from 'shared/types/habit-schedule';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';

import * as Styled from './habit-form.style';

export type CompletionPerTapInputProps = {
  schedules: HabitSchedule[];
  onChange: (schedules: HabitSchedule[]) => void;
  shouldReplaceSchedule?: boolean;
};

export const CompletionPerTapInput: React.FC<CompletionPerTapInputProps> = ({
  schedules,
  onChange,
  shouldReplaceSchedule,
}) => {
  const infoContainerRef = useRef<HTMLSpanElement>(null);
  const { t } = useTranslation();
  const today = useToday();
  const activeSchedule = useMemo(
    () => getActiveSchedule(schedules, today),
    [schedules, today],
  );

  const _onChange = () => {
    if (!activeSchedule) {
      return;
    }
    const updatedSchedule = {
      ...activeSchedule,
      action: activeSchedule.action
        ? null
        : { type: ScheduleCompletionActions.LogCompletionValue },
    };

    const endedSchedules = schedules.filter(({ endDate }) => !!endDate);
    const updateSchedules = [];

    if (!shouldReplaceSchedule) {
      const endDate = new Date();
      !!activeSchedule &&
        updateSchedules.push({
          ...activeSchedule,
          endDate,
          plannedOccurrences: activeSchedule.plannedOccurrences?.filter(
            ({ date }) => isBefore(date, startOfDay(endDate)),
          ),
        });
    }

    updateSchedules.push(updatedSchedule);

    onChange([...endedSchedules, ...updateSchedules]);
  };

  return (
    <>
      <Styled.Item $autoWidthField>
        <Styled.Label>
          <Icon icon={Keyboard16} />
          <Styled.LabelText>
            {t('forms.habit.fields.completions-per-checkbox.label')}
          </Styled.LabelText>
          <Styled.InfoIconContainer ref={infoContainerRef}>
            <Icon icon={Warning} />
          </Styled.InfoIconContainer>
        </Styled.Label>
        <Toggle
          value="manualCompletionTarget"
          checked={
            activeSchedule?.action?.type ===
            ScheduleCompletionActions.LogCompletionValue
          }
          onChange={_onChange}
        />
      </Styled.Item>
      <Tooltip
        label={t('forms.habit.fields.completions-per-checkbox.tooltip.label')}
        referenceElement={infoContainerRef}
      />
    </>
  );
};
